import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

const FilterPanel = ({ 
  onFiltersChange, 
  isCollapsed = false, 
  onToggleCollapse,
  departments = [],
  assetTypes = [],
  statuses = [],
  locations = []
}) => {
  const [filters, setFilters] = useState({
    dateRange: 'all',
    department: 'all',
    assetType: 'all',
    status: 'all',
    costRange: 'all',
    location: 'all',
    startDate: '',
    endDate: ''
  });

  const dateRangeOptions = [
    { value: 'all', label: 'All Time' },
    { value: '3months', label: 'Last 3 Months' },
    { value: '6months', label: 'Last 6 Months' },
    { value: '12months', label: 'Last 12 Months' },
    { value: '24months', label: 'Last 24 Months' },
    { value: 'custom', label: 'Custom Date Range' }
  ];

  const departmentOptions = [
    { value: 'all', label: 'All Departments' },
    ...departments.map(dept => ({ value: dept, label: dept }))
  ];

  const assetTypeOptions = [
    { value: 'all', label: 'All Asset Types' },
    ...assetTypes.map(type => ({ value: type, label: type }))
  ];

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    ...statuses.map(st => ({ 
      value: st, 
      label: st.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) 
    }))
  ];

  const costRangeOptions = [
    { value: 'all', label: 'All Price Ranges (INR)' },
    { value: '0-25000', label: 'Under ₹25,000' },
    { value: '25000-50000', label: '₹25,000 - ₹50,000' },
    { value: '50000-100000', label: '₹50,000 - ₹1,00,000' },
    { value: '100000-250000', label: '₹1,00,000 - ₹2,50,000' },
    { value: '250000+', label: 'Above ₹2,50,000' }
  ];

  const locationOptions = [
    { value: 'all', label: 'All Locations' },
    ...locations.map(loc => ({ value: loc, label: loc }))
  ];

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFiltersChange?.(newFilters);
  };

  const handleResetFilters = () => {
    const defaultFilters = {
      dateRange: 'all',
      department: 'all',
      assetType: 'all',
      status: 'all',
      costRange: 'all',
      location: 'all',
      startDate: '',
      endDate: ''
    };
    setFilters(defaultFilters);
    onFiltersChange?.(defaultFilters);
  };

  const hasActiveFilters = Object.entries(filters).some(([k, v]) => {
    if (k === 'startDate' || k === 'endDate') return Boolean(v);
    return v !== 'all';
  });

  if (isCollapsed) {
    return (
      <div className="bg-card border border-border rounded-lg p-4 shadow-subtle">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Icon name="Filter" size={20} className="text-primary" />
            <span className="font-medium text-card-foreground">Filters</span>
            {hasActiveFilters && (
              <span className="w-2 h-2 bg-primary rounded-full" />
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggleCollapse}
            className="h-8 w-8"
          >
            <Icon name="ChevronDown" size={16} />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-subtle">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Icon name="Filter" size={20} className="text-primary" />
          <h3 className="font-semibold text-card-foreground">Analytics Filters</h3>
          {hasActiveFilters && (
            <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-primary/10 text-primary border border-primary/20">
              Active
            </span>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleResetFilters}
            iconName="RotateCcw"
            disabled={!hasActiveFilters}
          >
            Reset Filters
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggleCollapse}
            className="h-8 w-8"
          >
            <Icon name="ChevronUp" size={16} />
          </Button>
        </div>
      </div>

      {/* Filter Controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <Select
          label="Purchase Period"
          options={dateRangeOptions}
          value={filters?.dateRange}
          onChange={(value) => handleFilterChange('dateRange', value)}
        />

        <Select
          label="Department"
          options={departmentOptions}
          value={filters?.department}
          onChange={(value) => handleFilterChange('department', value)}
        />

        <Select
          label="Asset Category"
          options={assetTypeOptions}
          value={filters?.assetType}
          onChange={(value) => handleFilterChange('assetType', value)}
        />

        <Select
          label="Status"
          options={statusOptions}
          value={filters?.status}
          onChange={(value) => handleFilterChange('status', value)}
        />

        <Select
          label="Cost Range (₹)"
          options={costRangeOptions}
          value={filters?.costRange}
          onChange={(value) => handleFilterChange('costRange', value)}
        />

        <Select
          label="Location"
          options={locationOptions}
          value={filters?.location}
          onChange={(value) => handleFilterChange('location', value)}
        />
      </div>

      {/* Custom Date Range */}
      {filters?.dateRange === 'custom' && (
        <div className="mt-4 p-4 bg-muted/40 rounded-lg border border-border">
          <h4 className="text-xs font-semibold text-card-foreground mb-3 uppercase tracking-wide">
            Select Custom Purchase Date Range
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Start Date"
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
            />
            <Input
              label="End Date"
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
            />
          </div>
        </div>
      )}

      {/* Filter Pills Summary */}
      {hasActiveFilters && (
        <div className="mt-4 pt-4 border-t border-border flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground mr-1">Active:</span>
          {Object.entries(filters).map(([key, value]) => {
            if (!value || value === 'all') return null;
            if (key === 'startDate' || key === 'endDate') {
              if (filters.dateRange !== 'custom') return null;
            }
            
            const label = key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1');
            return (
              <span
                key={key}
                className="inline-flex items-center space-x-1.5 px-2.5 py-1 bg-primary/10 border border-primary/20 text-primary text-xs rounded-full font-medium"
              >
                <span>{label}: <b>{value}</b></span>
                <button
                  type="button"
                  onClick={() => handleFilterChange(key, key === 'startDate' || key === 'endDate' ? '' : 'all')}
                  className="hover:bg-primary/20 rounded-full p-0.5 ml-0.5"
                >
                  <Icon name="X" size={12} />
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FilterPanel;
